import { useEffect, useState, useCallback } from 'react';
import { fetchRecentUpdates, LatestUpdate } from '../services/api/updatesService';

// publishedAt の ISO 文字列を保存。これより新しい update が1件以上あれば未読判定。
const SEEN_AT_KEY = 'richika_update_last_seen_at';
// 後方互換: 以前は id 単体を保存していた
const LEGACY_SEEN_ID_KEY = 'richika_update_last_seen_id';

function getLastSeenAt(): string | null {
  try {
    return window.localStorage.getItem(SEEN_AT_KEY);
  } catch {
    return null;
  }
}

function setLastSeenAt(iso: string): void {
  try {
    window.localStorage.setItem(SEEN_AT_KEY, iso);
    // レガシーキーは削除（データ移行）
    window.localStorage.removeItem(LEGACY_SEEN_ID_KEY);
  } catch {
    /* no-op: private browsing など */
  }
}

function parseIso(value: string | null): number {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * 直近 N 件のプロダクトアップデートを取得し、未読判定を行う hook。
 *
 * - `updates`: 取得したアップデート一覧（publishedAt desc）
 * - `hasUnread`: 最新の publishedAt > localStorage の last_seen_at なら true
 * - `markAllAsSeen`: 既読化（最新 publishedAt を保存 + セッション内再表示抑制）
 *
 * 同一セッション内で閉じたら再表示しない（`dismissedInSession`）。
 */
export function useRecentUpdates(limit: number = 5): {
  updates: LatestUpdate[];
  hasUnread: boolean;
  markAllAsSeen: () => void;
  isLoading: boolean;
} {
  const [updates, setUpdates] = useState<LatestUpdate[]>([]);
  const [dismissedInSession, setDismissedInSession] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const recent = await fetchRecentUpdates(limit);
      if (!cancelled) {
        setUpdates(recent);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [limit]);

  const lastSeenAt = getLastSeenAt();
  const latestPublishedAt = updates[0]?.published_at ?? null;

  const hasUnread = Boolean(
    updates.length > 0 &&
    !dismissedInSession &&
    parseIso(latestPublishedAt) > parseIso(lastSeenAt),
  );

  const markAllAsSeen = useCallback(() => {
    if (latestPublishedAt) {
      setLastSeenAt(latestPublishedAt);
    }
    setDismissedInSession(true);
  }, [latestPublishedAt]);

  return { updates, hasUnread, markAllAsSeen, isLoading };
}

// 後方互換: 既存呼び出し (`useLatestUpdate()`) を残す
export function useLatestUpdate(): {
  update: LatestUpdate | null;
  isUnread: boolean;
  markAsSeen: () => void;
} {
  const { updates, hasUnread, markAllAsSeen } = useRecentUpdates(1);
  return {
    update: updates[0] ?? null,
    isUnread: hasUnread,
    markAsSeen: markAllAsSeen,
  };
}
